import type { RealtimeEnvelope, RealtimeSubscribeRequest } from '@deskworks/shared-types';
import { useQueryClient, type QueryClient, type QueryKey } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { io, type Socket } from 'socket.io-client';
import { getToken, onTokenChange } from './api';
import { qk } from './query-keys';

export type RealtimeStatus = 'connecting' | 'connected' | 'disconnected';

interface RealtimeContextValue {
  status: RealtimeStatus;
  subscribe: (req: RealtimeSubscribeRequest) => () => void;
  /** Raw envelopes, for transient UI state that isn't in any query (e.g. a conversation's live activity line). */
  addListener: (listener: (env: RealtimeEnvelope) => void) => () => void;
}

const RealtimeContext = createContext<RealtimeContextValue | null>(null);

function subKey(req: RealtimeSubscribeRequest): string {
  return `${req.workspaceId}:${req.workItemId ?? ''}`;
}

/** Which query keys an envelope makes stale. The socket never writes data — it only invalidates. */
export function keysForEnvelope(env: RealtimeEnvelope): QueryKey[] {
  if (env.event === 'conversation.message' || env.event === 'conversation.updated') {
    const data = (env.data ?? {}) as { conversationId?: string; activity?: string; deliveredSeq?: number; title?: string };
    const keys: QueryKey[] = [];
    if (data.conversationId) keys.push(qk.conversation(data.conversationId));
    // A transient activity line or a read mark changes nothing in the desk's conversation list.
    if (env.deskId && !(env.event === 'conversation.updated' && (data.activity || data.deliveredSeq))) keys.push(qk.deskConversations(env.deskId));
    // A setup chat its assistant just named: the Start page lists it with the new title.
    if (!env.deskId && data.title) keys.push(qk.setupConversations(env.workspaceId));
    return keys;
  }
  // A batch of session steps changes nothing but the session itself.
  if (env.event === 'work_item.transcript' || env.event === 'session.transcript') {
    const data = (env.data ?? {}) as { executionId?: string; conversationId?: string | null };
    const keys: QueryKey[] = data.executionId ? [qk.session(data.executionId)] : [];
    if (env.workItemId) keys.push(qk.workItemTranscript(env.workItemId));
    else if (data.conversationId) keys.push(qk.conversationTranscript(data.conversationId));
    else if (env.deskId) keys.push(qk.deskTranscript(env.deskId));
    return keys;
  }
  // Files changed on main or in a Work Item's copy: only the files' views (the merge's note comes as activity).
  if (env.event === 'desk.files') {
    const keys: QueryKey[] = env.deskId ? [qk.deskFiles(env.deskId)] : [];
    if (env.workItemId) keys.push(qk.workItemFiles(env.workItemId));
    return keys;
  }
  const keys: QueryKey[] = [
    qk.overview(env.workspaceId),
    qk.activityAll(env.workspaceId),
    qk.workItemsAll(env.workspaceId),
    qk.desks(env.workspaceId),
    qk.approvalsAll(env.workspaceId),
    qk.reportsAll(env.workspaceId),
  ];
  if (env.deskId) keys.push(qk.desk(env.deskId));
  if (env.workItemId) keys.push(qk.workItem(env.workItemId));
  // A call asked for in a chat that writes (its approval card lives in the chat).
  const actionChat = env.event === 'action.updated' ? (env.data as { conversationId?: string | null } | null)?.conversationId : null;
  if (actionChat) keys.push(qk.conversationActions(actionChat));
  if (env.event.startsWith('agent.') || env.event.startsWith('execution.') || env.event === 'work_item.updated') {
    keys.push(qk.agentAll, qk.sessionsAll(env.workspaceId));
    const executionId = (env.data as { executionId?: string } | null)?.executionId;
    if (executionId) keys.push(qk.session(executionId));
  }
  // A run ended: its tokens/cost and a subscription's plan limits changed.
  if (env.event === 'agent.completed' || env.event === 'agent.waiting' || env.event.startsWith('execution.')) {
    keys.push(qk.usageAll(env.workspaceId), qk.runtimeProfiles(env.workspaceId));
  }
  // "Needs you" inbox: questions (waiting + agent messages), approvals, failures.
  if (
    env.accountId &&
    (env.event === 'work_item.updated' ||
      env.event === 'work_item.message' ||
      env.event.startsWith('approval.') ||
      env.event === 'action.updated' ||
      env.event === 'agent.waiting' ||
      env.event === 'execution.failed')
  ) {
    keys.push(qk.accountInbox(env.accountId));
  }
  // Account-wide desk list (status, lead, open counts). Chat/activity lines don't change it.
  if (env.accountId && env.event !== 'work_item.activity' && env.event !== 'work_item.message') {
    keys.push(qk.accountDesks(env.accountId));
  }
  return keys;
}

/**
 * Collects invalidations for a short moment so that a burst of activity events
 * causes one refetch per query instead of one per event.
 */
function createInvalidator(queryClient: QueryClient) {
  const pending = new Map<string, QueryKey>();
  let timer: number | null = null;
  return (keys: QueryKey[]) => {
    for (const k of keys) pending.set(JSON.stringify(k), k);
    if (timer !== null) return;
    timer = window.setTimeout(() => {
      timer = null;
      const batch = [...pending.values()];
      pending.clear();
      for (const queryKey of batch) void queryClient.invalidateQueries({ queryKey });
    }, 250);
  };
}

export function RealtimeProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<RealtimeStatus>('disconnected');
  const [token, setTokenState] = useState<string | null>(getToken);
  const socketRef = useRef<Socket | null>(null);
  const subsRef = useRef(new Map<string, { req: RealtimeSubscribeRequest; count: number }>());
  const listenersRef = useRef(new Set<(env: RealtimeEnvelope) => void>());

  useEffect(() => onTokenChange(setTokenState), []);

  useEffect(() => {
    if (!token) {
      setStatus('disconnected');
      return;
    }
    const invalidate = createInvalidator(queryClient);
    const socket = io('/realtime', {
      path: '/socket.io',
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
    });
    socketRef.current = socket;
    setStatus('connecting');
    let wasConnected = false;

    socket.on('connect', () => {
      setStatus('connected');
      for (const { req } of subsRef.current.values()) socket.emit('subscribe', req);
      // After a reconnect we may have missed events — refresh whatever is on screen.
      if (wasConnected) void queryClient.invalidateQueries();
      wasConnected = true;
    });
    socket.on('disconnect', () => setStatus('disconnected'));
    socket.on('connect_error', () => setStatus('disconnected'));
    socket.io.on('reconnect_attempt', () => setStatus('connecting'));
    socket.on('deskworks', (env: RealtimeEnvelope) => {
      if (!env || typeof env !== 'object' || !env.workspaceId) return;
      invalidate(keysForEnvelope(env));
      for (const l of listenersRef.current) l(env);
    });

    return () => {
      socket.removeAllListeners();
      socket.io.removeAllListeners();
      socket.disconnect();
      socketRef.current = null;
    };
  }, [token, queryClient]);

  const subscribe = useRef((req: RealtimeSubscribeRequest) => {
    const key = subKey(req);
    const existing = subsRef.current.get(key);
    if (existing) existing.count++;
    else {
      subsRef.current.set(key, { req, count: 1 });
      if (socketRef.current?.connected) socketRef.current.emit('subscribe', req);
    }
    return () => {
      const entry = subsRef.current.get(key);
      if (!entry) return;
      entry.count--;
      if (entry.count <= 0) {
        subsRef.current.delete(key);
        if (socketRef.current?.connected) socketRef.current.emit('unsubscribe', req);
      }
    };
  }).current;

  const addListener = useRef((listener: (env: RealtimeEnvelope) => void) => {
    listenersRef.current.add(listener);
    return () => {
      listenersRef.current.delete(listener);
    };
  }).current;

  return <RealtimeContext.Provider value={{ status, subscribe, addListener }}>{children}</RealtimeContext.Provider>;
}

export function useRealtimeStatus(): RealtimeStatus {
  return useContext(RealtimeContext)?.status ?? 'disconnected';
}

/** Joins the workspace room (and optionally a work item room) while the component is mounted. */
export function useRealtimeSubscription(workspaceId: string | undefined, workItemId?: string) {
  const ctx = useContext(RealtimeContext);
  const subscribe = ctx?.subscribe;
  useEffect(() => {
    if (!subscribe || !workspaceId) return;
    return subscribe(workItemId ? { workspaceId, workItemId } : { workspaceId });
  }, [subscribe, workspaceId, workItemId]);
}

/** Joins several workspace rooms while mounted (e.g. those of the desks a chat's questions went to). */
export function useRealtimeSubscriptions(workspaceIds: string[]) {
  const subscribe = useContext(RealtimeContext)?.subscribe;
  const key = [...new Set(workspaceIds)].sort().join(',');
  useEffect(() => {
    if (!subscribe || !key) return;
    const leave = key.split(',').map((workspaceId) => subscribe({ workspaceId }));
    return () => leave.forEach((off) => off());
  }, [subscribe, key]);
}

/** Calls `handler` for every realtime envelope while mounted (latest handler is always used). */
export function useRealtimeEnvelope(handler: (env: RealtimeEnvelope) => void) {
  const ctx = useContext(RealtimeContext);
  const ref = useRef(handler);
  ref.current = handler;
  const addListener = ctx?.addListener;
  useEffect(() => {
    if (!addListener) return;
    return addListener((env) => ref.current(env));
  }, [addListener]);
}
